import { Flex } from "@/components/atoms/flex";
import { useIntl } from "react-intl";
import { mergeClasses, Text } from "@fluentui/react-components";
import { InteractionActionsProps } from "./types";
import { InteractionType } from "@/common/types";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import Timer from "@/components/atoms/timer/Timer";
import { VoicemailInProgressActionButtons } from "./voicemailInProgressActionButtons";
import { InteractionDirection } from "@/repository/models";
import {
	EmailInProgressActionButtons,
	GenericInProgressActionButtons,
	WhatsAppInProgressActionButtons,
} from "./messagingInProgressActionButtons";
import { useCallback, useMemo } from "react";
import { useTextMonitoring } from "@/hooks/supervisor";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { WebchatInProgressActionButtons } from "./messagingInProgressActionButtons/WebchatInProgressActionButtons";
import { EmailPreviewActionButtons } from "./messagingInProgressActionButtons/EmailPreviewActionButtons";

export const InteractionActions = (props: InteractionActionsProps) => {
	const { type } = props;
	const intl = useIntl();
	const interactionBarCommonStyle = useInteractionBarCommonStyle();

	const currentInteractionState = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state
	);

	const currentInteraction = useMemo(
		() =>
			currentInteractionState.preview
				? currentInteractionState.preview
				: currentInteractionState.active,
		[currentInteractionState]
	);

	const isPreview = useMemo(
		() => Boolean(currentInteractionState.preview),
		[currentInteractionState]
	);

	const direction =
		currentInteraction?.direction == InteractionDirection.Outbound
			? "Outbound"
			: "Ongoing";

	const actionsMap = useMemo(
		() => ({
			[InteractionType.Voicemail]: <VoicemailInProgressActionButtons />,
			[InteractionType.Generic]: <GenericInProgressActionButtons />,
			[InteractionType.Email]: <EmailInProgressActionButtons />,
			[InteractionType.Call]: undefined,
			[InteractionType.WhatsApp]: <WhatsAppInProgressActionButtons />,
			[InteractionType.Webchat]: <WebchatInProgressActionButtons />,
		}),
		[]
	);

	const timerStatus = useMemo(() => {
		if (currentInteraction?.isOnHold) {
			return (
				<Text
					align="end"
					className={interactionBarCommonStyle.onHoldText}
					children={intl.formatMessage({
						id: `Interaction.Operations.Parked`,
					})}
				/>
			);
		}

		return (
			<Timer
				className={interactionBarCommonStyle.timer}
				startDate={currentInteraction?.startDate}
			/>
		);
	}, [currentInteraction, type]);

	const isTextMonitoring = useTextMonitoring();

	const getOperationName = useCallback(() => {
		return intl.formatMessage({
			id: `Interaction.Operations.${isTextMonitoring ? "Listen" : `${direction}${InteractionType[type]}`}`,
		});
	}, [currentInteraction]);

	return (
		<div
			className={mergeClasses(
				interactionBarCommonStyle.interactionText,
				interactionBarCommonStyle.interactionTimerAndButtonsContainer
			)}
		>
			<Flex className={interactionBarCommonStyle.interactionActionContainer}>
				<Flex column className={interactionBarCommonStyle.interactionAction}>
					<Text
						align="end"
						className={interactionBarCommonStyle.actionText}
						children={
							isPreview
								? intl.formatMessage({ id: "Interaction.Operations.Preview" })
								: getOperationName()
						}
					/>

					<Text
						align="end"
						className={mergeClasses(
							interactionBarCommonStyle.actionText,
							interactionBarCommonStyle.queueName
						)}
						title={currentInteraction?.queueName}
					>
						{currentInteraction?.queueName}
					</Text>
				</Flex>
				<Flex vAlign="center" className={interactionBarCommonStyle.timer}>
					{timerStatus}
				</Flex>
			</Flex>
			{isPreview ? <EmailPreviewActionButtons /> : actionsMap[type]}
		</div>
	);
};
